import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  DEFAULT_VARIANT_TYPE,
  DRAFT_WRITE_DELAY_MS,
  SHOP_KEY,
  buildInitialState,
  buildPayload,
  buildVariantRow,
  clearDraft,
  getDraftKey,
  normalizeImageArray,
  readDraft,
  validateForm,
  writeDraft,
} from "./ReadyMadeProductForm.shared";
import { getSafeUserMessage } from "../../../../utils/errorMessages";

function getConflictMessage(errorMeta, error) {
  if (Number(errorMeta?.status) !== 409) return "";

  switch (String(errorMeta?.code || "")) {
    case "VERSION_CONFLICT":
      return "This product was changed elsewhere. Reload before saving.";
    case "PRODUCT_SKU_EXISTS":
      return "A product or variant with this SKU already exists.";
    case "PRODUCT_SLUG_EXISTS":
      return "A product with this slug already exists in this shop.";
    case "PRODUCT_EXTERNAL_ID_EXISTS":
      return "A product with this external ID already exists in this shop.";
    default:
      return error || "Conflict detected.";
  }
}

export default function useReadyMadeProductForm({
  initialData,
  onSubmit,
  loading,
  error,
  errorMeta,
  saveSuccess,
  shopKey,
}) {
  const draftKey = useMemo(() => getDraftKey(initialData), [initialData]);

  const hydratedRef = useRef(false);
  const writeTimerRef = useRef(null);
  const initialDraftRef = useRef(null);

  const [form, setForm] = useState(() => {
    const savedDraft = readDraft(draftKey);
    initialDraftRef.current = savedDraft;

    return buildInitialState(initialData, savedDraft);
  });

  const [submitError, setSubmitError] = useState("");
  const [draftRestored, setDraftRestored] = useState(
    Boolean(initialDraftRef.current),
  );
  const [isDirty, setIsDirty] = useState(Boolean(initialDraftRef.current));

  const clearDraftWriteTimer = useCallback(() => {
    if (typeof window === "undefined") return;

    if (writeTimerRef.current) {
      window.clearTimeout(writeTimerRef.current);
      writeTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const savedDraft = readDraft(draftKey);

    setForm(buildInitialState(initialData, savedDraft));
    setSubmitError("");
    setDraftRestored(Boolean(savedDraft));
    setIsDirty(Boolean(savedDraft));

    hydratedRef.current = true;
  }, [draftKey, initialData]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!hydratedRef.current || !isDirty) return undefined;

    clearDraftWriteTimer();

    writeTimerRef.current = window.setTimeout(() => {
      writeDraft(draftKey, form);
      writeTimerRef.current = null;
    }, DRAFT_WRITE_DELAY_MS);

    return clearDraftWriteTimer;
  }, [clearDraftWriteTimer, draftKey, form, isDirty]);

  useEffect(() => {
    if (!saveSuccess) return;

    clearDraftWriteTimer();
    clearDraft(draftKey);
    setDraftRestored(false);
    setIsDirty(false);
  }, [clearDraftWriteTimer, draftKey, saveSuccess]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handler = (event) => {
      if (loading || !isDirty) return;

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handler);

    return () => {
      window.removeEventListener("beforeunload", handler);
    };
  }, [isDirty, loading]);

  const markDirty = useCallback(() => {
    setSubmitError("");
    setIsDirty(true);
  }, []);

  const setField = useCallback(
    (key, value) => {
      markDirty();

      setForm((prev) => {
        if (Object.is(prev[key], value)) return prev;
        return { ...prev, [key]: value };
      });
    },
    [markDirty],
  );

  const setVariantField = useCallback(
    (index, key, value) => {
      markDirty();

      setForm((prev) => ({
        ...prev,
        variants: prev.variants.map((variant, currentIndex) =>
          currentIndex === index ? { ...variant, [key]: value } : variant,
        ),
      }));
    },
    [markDirty],
  );

  const setImages = useCallback(
    (images) => {
      markDirty();

      setForm((prev) => ({
        ...prev,
        images: normalizeImageArray(
          typeof images === "function" ? images(prev.images) : images,
        ),
      }));
    },
    [markDirty],
  );

  const addVariant = useCallback(() => {
    markDirty();

    setForm((prev) => ({
      ...prev,
      variants: [...prev.variants, buildVariantRow()],
    }));
  }, [markDirty]);

  const removeVariant = useCallback(
    (index) => {
      markDirty();

      setForm((prev) => ({
        ...prev,
        variants:
          prev.variants.length > 1
            ? prev.variants.filter((_, currentIndex) => currentIndex !== index)
            : [buildVariantRow({ variantType: DEFAULT_VARIANT_TYPE })],
      }));
    },
    [markDirty],
  );

  const discardDraft = useCallback(() => {
    clearDraftWriteTimer();
    clearDraft(draftKey);
    setForm(buildInitialState(initialData, null));
    setSubmitError("");
    setDraftRestored(false);
    setIsDirty(false);
  }, [clearDraftWriteTimer, draftKey, initialData]);

  const submitDisabled = Boolean(loading);

  const conflictMessage = useMemo(
    () => getConflictMessage(errorMeta, error),
    [error, errorMeta],
  );

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (loading) return;

      const validationError = validateForm(form);

      if (validationError) {
        setSubmitError(validationError);
        return;
      }

      const payload = buildPayload({
        ...form,
        shopKey: shopKey || SHOP_KEY,
      });

      try {
        await onSubmit(payload);

        clearDraftWriteTimer();
        clearDraft(draftKey);
        setDraftRestored(false);
        setIsDirty(false);
      } catch (submitFailure) {
        setSubmitError(
          getSafeUserMessage(
            submitFailure?.message || error,
            "We could not save this ready-made product right now. Please try again later.",
          ),
        );
      }
    },
    [clearDraftWriteTimer, draftKey, error, form, loading, onSubmit, shopKey],
  );

  return {
    form,
    draftRestored,
    submitError,
    submitDisabled,
    conflictMessage,
    setField,
    setVariantField,
    setImages,
    addVariant,
    removeVariant,
    discardDraft,
    handleSubmit,
  };
}
